import { supabase } from '../../supabase';
import { writeAuditEvent, AUDIT_ENTITY_TYPES, AUDIT_EVENT_TYPES } from '../auditEvents';
import {
  ESTIMATE_RESPONSES_SETTINGS_KEY,
  ESTIMATE_RESPONSE_NOTIFY_SETTINGS_KEY,
  diffEstimateResponsesToggle,
  diffEstimateResponseNotifyUsers,
} from './estimateResponseSettings';
import {
  OFFICE_TASK_NOTIFY_SETTINGS_KEY,
  diffOfficeTaskNotifyUsers,
} from './officeTaskSettings';

/**
 * Everything `saveCompanySettings` emits to the audit trail, in one place.
 *
 * Extracted from `companySettingsService.js` when the "also notify" list pushed
 * that file past the 500-line cap (extract before adding). It is a clean seam:
 * the service performs the WRITE, this decides what the write MEANT.
 *
 * *** THE SPLIT INTO THREE EVENTS IS A RULE, NOT A STYLE CHOICE. ***
 * One save press can change three independent things, and each has a different
 * reader:
 *   • the estimate-response TOGGLE decides whether an unauthenticated link can
 *     move an estimate to accepted/declined — a security-policy peer of
 *     `mfa_policy_changed`, and it must not be buried inside a generic
 *     "Settings changed / Changed fields: …" line;
 *   • the "also notify" LIST answers "who added Carla to the estimate
 *     notifications, and when" — likewise unanswerable from a field name;
 *   • everything else is the ordinary `settings:edited` funnel.
 * Both special keys are therefore EXCLUDED from `diffSettingsKeys`, or one
 * action would land in two rows — the duplicate-emission shape
 * `auditSingleEmission.test.js` exists to prevent.
 *
 * Every emit is fire-and-forget: a failed audit must never fail a save, which
 * is exactly why the DB values (not these rows) are what the server reads.
 */

/** Field-level diff for the audit payload — returns the list of editable
 *  setting keys whose value changed between prior and next. Treats
 *  null/undefined/'' as the same empty state so an unchanged-but-coerced
 *  field doesn't show as a spurious edit. */
export function diffSettingsKeys(prior, next, editableKeys) {
  const isEmpty = (v) => v === null || v === undefined || v === '';
  const out = [];
  (editableKeys || []).forEach((key) => {
    // Excluded — these two emit their own rows (see the docblock above).
    if (key === ESTIMATE_RESPONSES_SETTINGS_KEY) return;
    // The notify list ALSO cannot be compared with `!==` at all: it is an array,
    // so every save would report it as changed on object identity alone.
    if (key === ESTIMATE_RESPONSE_NOTIFY_SETTINGS_KEY) return;
    // RD-ROLE-006 — same array-identity reason, same exclusion.
    if (key === OFFICE_TASK_NOTIFY_SETTINGS_KEY) return;
    const a = prior[key];
    const b = next[key];
    if (isEmpty(a) && isEmpty(b)) return;
    if (a !== b) out.push(key);
  });
  return out;
}

/**
 * The "also notify" row, with NAMES denormalized into the payload.
 *
 * *** IDS ALONE WOULD MAKE THE EVENT UNREADABLE, AND THAT IS THE ONLY THING
 * THIS EVENT IS FOR. *** The stored setting is user ids (stable across renames
 * and offboarding, and the only thing safe to act on), but "who was told when
 * Carla approved?" cannot be answered by a pair of uuids on a screen. The house
 * pattern is exactly this: `enrich_audit_event_payload` denormalizes entity
 * context so the Activity Log reads without joins, and `settings:edited`
 * snapshots `previous_name` for the same reason.
 *
 * The names are a SNAPSHOT, deliberately kept alongside the ids rather than
 * replacing them — a person renamed or removed later must not rewrite what an
 * old audit row says, and the ids stay authoritative for forensics.
 *
 * Fail-soft in every direction: this is fire-and-forget, so a failed lookup
 * simply emits the ids without names rather than losing the event. The renderer
 * falls back to a short id.
 */
/**
 * The names lookup only — shared by both "also notify" emitters below.
 *
 * *** DELIBERATELY NOT SHARING THE `writeAuditEvent` CALL TOO. ***
 * `auditEventCoverage.derived.test.js` statically scans every `writeAuditEvent`
 * call site and requires `eventType` to resolve to a literal or an
 * `AUDIT_EVENT_TYPES.X` member expression — it does not trace a value through
 * a function parameter. An earlier version of this file passed `eventType` as
 * a third argument to one shared emit helper, which read cleanly but made the
 * literal invisible at the call the scanner inspects ("New unresolved
 * writeAuditEvent call site(s)... eventType=eventType"). Each event keeps its
 * OWN tiny `writeAuditEvent` call with the constant spelled out, exactly like
 * the toggle emit above it.
 */
async function resolveNotifyChangeNames(notifyChange) {
  const ids = [...new Set([...(notifyChange.previous || []), ...(notifyChange.user_ids || [])])];
  let names;
  try {
    if (ids.length > 0) {
      const { data } = await supabase.from('profiles').select('id, full_name').in('id', ids);
      if (Array.isArray(data) && data.length > 0) {
        names = {};
        data.forEach((p) => { if (p?.id && p.full_name) names[p.id] = p.full_name; });
      }
    }
  } catch (_) { /* names are a convenience; the event is the point */ }
  return names;
}

async function emitEstimateResponseNotifyChange(tenantId, notifyChange) {
  const names = await resolveNotifyChangeNames(notifyChange);
  void writeAuditEvent({
    tenantId,
    entityType: AUDIT_ENTITY_TYPES.SETTINGS,
    entityId: tenantId,
    eventType: AUDIT_EVENT_TYPES.SETTINGS_ESTIMATE_RESPONSE_NOTIFY_CHANGED,
    payload: names ? { ...notifyChange, names } : notifyChange,
  });
}

/** RD-ROLE-006 — the Office Inbox twin of the emitter above. */
async function emitOfficeTaskNotifyChange(tenantId, notifyChange) {
  const names = await resolveNotifyChangeNames(notifyChange);
  void writeAuditEvent({
    tenantId,
    entityType: AUDIT_ENTITY_TYPES.SETTINGS,
    entityId: tenantId,
    eventType: AUDIT_EVENT_TYPES.SETTINGS_OFFICE_TASK_NOTIFY_CHANGED,
    payload: names ? { ...notifyChange, names } : notifyChange,
  });
}

/**
 * The two "also notify" list rows ONLY (names denormalized client-side).
 *
 * Split out so the RPC save path (`update_company_settings`, which audits
 * `settings:edited` and the estimate-responses toggle SERVER-side) can still emit
 * these two — the DB deliberately does not look up user names — without also
 * re-emitting the events the server already wrote (single emission).
 */
export function emitNotifyListAuditEvents({ tenantId, priorSettings, settings }) {
  const notifyChange = diffEstimateResponseNotifyUsers(priorSettings, settings);
  if (notifyChange) void emitEstimateResponseNotifyChange(tenantId, notifyChange);

  // RD-ROLE-006 — the Office Inbox "also notify" list, same treatment.
  const officeNotifyChange = diffOfficeTaskNotifyUsers(priorSettings, settings);
  if (officeNotifyChange) void emitOfficeTaskNotifyChange(tenantId, officeNotifyChange);
}

/**
 * `entityId` is ALWAYS the tenant uuid, never `company_settings.id`.
 * That column is the singleton text key `'default'`; writing it into a uuid
 * column raised 22P02 and — because `writeAuditEvent` is fire-and-forget —
 * silently dropped the event with no error anywhere.
 */
export function emitCompanySettingsAuditEvents({ tenantId, priorSettings, settings, editableKeys }) {
  const responsesChange = diffEstimateResponsesToggle(priorSettings, settings);
  if (responsesChange) {
    void writeAuditEvent({
      tenantId,
      entityType: AUDIT_ENTITY_TYPES.SETTINGS,
      entityId: tenantId,
      eventType: AUDIT_EVENT_TYPES.SETTINGS_ESTIMATE_RESPONSES_POLICY_CHANGED,
      payload: responsesChange,
    });
  }

  emitNotifyListAuditEvents({ tenantId, priorSettings, settings });

  const changedFields = diffSettingsKeys(priorSettings, settings, editableKeys);
  if (changedFields.length === 0) return;

  // Single funnel for "company settings changed" — covers branding form saves,
  // logo uploads, logo resets, and any other surface that goes through
  // saveCompanySettings. Field-level diff is the audit signal; we deliberately
  // don't snapshot raw values (some are PII-adjacent like phone/address/email —
  // entity_id + diff is enough).
  void writeAuditEvent({
    tenantId,
    entityType: AUDIT_ENTITY_TYPES.SETTINGS,
    entityId: tenantId,
    eventType: 'edited',
    payload: {
      changed_fields: changedFields,
      // Narrow, deliberate exception to the no-raw-values rule above: the
      // COMPANY NAME is not PII-adjacent — it is printed on every invoice and
      // email the customer already receives — and "who renamed us, and from
      // what?" is the one settings question the diff alone cannot answer.
      // Phone/address/email stay diff-only. Same key names as the platform-side
      // tenant_profile_changed event so one renderer covers both.
      ...(changedFields.includes('companyName')
        ? {
          previous_name: priorSettings?.companyName ?? null,
          name: settings.companyName ?? null,
        }
        : {}),
      ...(changedFields.includes('companyLogoUrl')
        ? { logo_state: settings.companyLogoUrl ? 'set' : 'cleared' }
        : {}),
    },
  });
}
